import React, { useMemo, useCallback } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
  ReferenceLine
} from 'recharts';
import selectors from '../../../store/appSelectors';
import actions from '../../../store/appActions';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Derive isoStart / isoEnd from a MarkLogic dynamic-facet bucket name.
 *
 * Geert's server-side implementation returns:
 *   "2026"       → year bucket
 *   "2026-05"    → month bucket
 *   "2026-05-23" → day bucket
 *
 * We use moment to derive the end of each period for click-to-filter.
 */
function bucketNameToRange(name) {
  if (!name) return { isoStart: null, isoEnd: null, label: name };
  const parts = name.split('-');
  let unit, labelFmt;
  if (parts.length === 1) {
    unit = 'year';
    labelFmt = 'YYYY';
  } else if (parts.length === 2) {
    unit = 'month';
    labelFmt = 'MMM YYYY';
  } else {
    unit = 'day';
    labelFmt = 'D MMM YYYY';
  }
  const m = moment(name, ['YYYY', 'YYYY-MM', 'YYYY-MM-DD'], true);
  if (!m.isValid()) return { isoStart: null, isoEnd: null, label: name };
  return {
    label: m.format(labelFmt),
    isoStart: m.startOf(unit).toISOString(),
    isoEnd: m
      .clone()
      .add(1, unit)
      .toISOString()
  };
}

/**
 * Parse the aggregates response into histogram buckets.
 *
 * Primary path: aggregates.dateHistogram  populated by getAggregates() from
 *   response.facets.dateTime — standard MarkLogic facet format:
 *   { facetValues: [{ name: "2026", count: 5 }, ...] }
 *
 * Fallback: any key with a facetValues array (legacy / future formats).
 */
function parseBuckets(aggregates) {
  if (!aggregates || typeof aggregates !== 'object') return [];

  // Primary: dateHistogram from Geert's dynamic dateTime facet
  const hist = aggregates.dateHistogram;
  if (hist && Array.isArray(hist.facetValues) && hist.facetValues.length) {
    return hist.facetValues
      .map(v => ({
        ...bucketNameToRange(v.name),
        isoStart: v.lowerBound || v.minimum,
        isoEnd: v.upperBound || v.maximum,
        count: Number(v.count) || 0
      }))
      .filter(b => b.isoStart !== null);
  }

  // Legacy fallback
  for (const key of Object.keys(aggregates)) {
    const facet = aggregates[key];
    if (facet && Array.isArray(facet.facetValues) && facet.facetValues.length) {
      return facet.facetValues.map((v, i) => ({
        label: v.name || v.value || String(i),
        isoStart: v.value || null,
        isoEnd: facet.facetValues[i + 1]
          ? facet.facetValues[i + 1].value
          : null,
        count: Number(v.count) || 0
      }));
    }
  }
  return [];
}

// ---------------------------------------------------------------------------
// Custom tooltip// ---------------------------------------------------------------------------
// Custom tooltip
// ---------------------------------------------------------------------------
const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const { label, count } = payload[0].payload;
  return (
    <div className="histogram-tooltip">
      <span className="histogram-tooltip__date">{label}</span>
      <span className="histogram-tooltip__count">
        {count.toLocaleString()} obs
      </span>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Skeleton (shown while aggregates are loading)
// ---------------------------------------------------------------------------
const SkeletonBars = () => (
  <div className="histogram-skeleton" aria-hidden="true">
    {[40, 65, 50, 80, 55, 90, 70, 45, 75, 60, 85, 50, 40, 70, 60, 80, 95].map(
      (h, i) => (
        <div
          key={i}
          className="histogram-skeleton__bar"
          style={{ height: `${h}%` }}
        />
      )
    )}
  </div>
);

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
const HistogramComponent = ({
  aggregates,
  aggregatesFetching,
  queryStart,
  queryEnd,
  setStartEnd
}) => {
  const buckets = useMemo(() => parseBuckets(aggregates), [aggregates]);

  const handleBarClick = useCallback(
    data => {
      if (!data || !data.activePayload || !data.activePayload.length) return;
      const bucket = data.activePayload[0].payload;
      if (!bucket.isoStart) return;
      const end = bucket.isoEnd || bucket.isoStart;
      setStartEnd(bucket.isoStart, end);
    },
    [setStartEnd]
  );

  const totalInRange = useMemo(() => {
    if (!queryStart || !queryEnd || !buckets.length) return null;
    return buckets
      .filter(b => b.isoStart >= queryStart && b.isoStart < queryEnd)
      .reduce((sum, b) => sum + b.count, 0);
  }, [buckets, queryStart, queryEnd]);

  if (aggregatesFetching) {
    return (
      <div
        className="histogram-chart-wrap"
        role="img"
        aria-label="Loading Observation timeline chart"
      >
        <SkeletonBars />
      </div>
    );
  }

  if (!buckets.length) {
    return (
      <div
        className="histogram-chart-wrap histogram-chart-wrap--empty"
        role="img"
        aria-label="No observation data available for the current query"
      >
        <span className="histogram-empty-msg">No data</span>
      </div>
    );
  }

  const ariaLabel =
    `Observation timeline histogram with ${buckets.length} time buckets. ` +
    `Total observations: ${buckets
      .reduce((s, b) => s + b.count, 0)
      .toLocaleString()}.` +
    (totalInRange !== null
      ? ` ${totalInRange.toLocaleString()} in selected range.`
      : '') +
    ' Click a bar to filter by that date bucket.';

  return (
    <div className="histogram-chart-wrap" role="img" aria-label={ariaLabel}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={buckets}
          onClick={handleBarClick}
          margin={{ top: 4, right: 6, bottom: 14, left: 0 }}
          barCategoryGap="15%"
        >
          <XAxis
            dataKey="label"
            tick={{ fontSize: 9, fill: '#666' }}
            interval="preserveStartEnd"
            tickLine={false}
            axisLine={{ stroke: '#ddd' }}
          />
          <YAxis hide domain={[0, 'auto']} />
          <Tooltip
            content={<CustomTooltip />}
            cursor={{ fill: 'rgba(0,77,64,0.06)' }}
          />
          {queryStart && (
            <ReferenceLine
              x={queryStart}
              stroke="#FFAB00"
              strokeWidth={1.5}
              strokeDasharray="3 3"
            />
          )}
          {queryEnd && (
            <ReferenceLine
              x={queryEnd}
              stroke="#FFAB00"
              strokeWidth={1.5}
              strokeDasharray="3 3"
            />
          )}
          <Bar
            dataKey="count"
            radius={[2, 2, 0, 0]}
            isAnimationActive={false}
            aria-hidden="true"
          >
            {buckets.map((b, i) => {
              const inRange =
                queryStart && queryEnd
                  ? b.isoStart >= queryStart && b.isoStart < queryEnd
                  : true;
              return (
                <Cell
                  key={i}
                  fill={inRange ? '#004D40' : '#a8c8c4'}
                  cursor="pointer"
                />
              );
            })}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Redux wiring
// ---------------------------------------------------------------------------
const mapStateToProps = state => ({
  aggregates: selectors.timeline.aggregates(state.timeline),
  aggregatesFetching: selectors.timeline.aggregatesFetching(state.timeline),
  queryStart: state.query.start,
  queryEnd: state.query.end
});

const mapDispatchToProps = dispatch => ({
  setStartEnd: (start, end) => dispatch(actions.query.setStartEnd(start, end))
});

export default connect(mapStateToProps, mapDispatchToProps)(HistogramComponent);
